import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, FolderInput, Trash2 } from 'lucide-react';

/**
 * The context menu for a MULTI-row selection in the collections tree.
 *
 * Deliberately a separate `menu.type` in `Sidebar.renderContextMenu` rather than
 * a branch woven into the per-item body: that body is a chain of per-type
 * entries (Add request / New folder / Rename / Duplicate / Generate code / Run /
 * Export / Reveal / Share) which all assume `menu.item` is ONE record, and it
 * ends in an ungated Delete. The header menu is already early-returned for the
 * same reason; this follows that precedent.
 *
 * Markup and classes are the sidebar's own (`ctx-menu-btn`,
 * `ctx-menu-btn--danger`) — this menu is NOT migrated onto `ui/ContextMenu.js`,
 * which has no concept of the hover flyouts the sibling menu uses.
 *
 * Three actions exist, and that is the whole point of the feature: bulk
 * delete, bulk move and bulk duplicate are the jobs where doing it one at a time
 * is the bottleneck (post-import triage; "copy these six into the next
 * environment's folder" — owner, 18.09.2026). Bulk run is a runner feature in
 * disguise, and
 * a bulk property edit would write N copies of a value that folder-level auth
 * inheritance already models correctly — both are deliberately absent.
 */
export default function SelectionContextMenu({
  x,
  y,
  menuRef,
  summary,
  onDelete,
  onMove,
  onDuplicate,
}) {
  const { t } = useTranslation();
  const { total, canDelete, canMove, canDuplicate, readOnly, hasCollection } = summary;

  const moveTitle = readOnly
    ? t('sidebar.selectionReadOnly')
    : hasCollection
      ? t('sidebar.selectionHasCollection')
      : undefined;

  return (
    <div
      ref={menuRef}
      data-testid="selection-context-menu"
      className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
      style={{ left: x, top: y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* index.css is a hand-rolled utility subset, not Tailwind: `text-[11px]`
          is NOT in it and would silently no-op, so this uses `text-xs`. */}
      <div className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">
        {t('sidebar.selectionCount', { count: total })}
      </div>
      <button
        type="button"
        disabled={!canMove}
        title={moveTitle}
        onClick={onMove}
        className="ctx-menu-btn"
      >
        <FolderInput size={14} />
        <span>{t('sidebar.moveSelected')}</span>
      </button>
      <button
        type="button"
        disabled={!canDuplicate}
        title={readOnly ? t('sidebar.selectionReadOnly') : undefined}
        onClick={onDuplicate}
        className="ctx-menu-btn"
        data-testid="selection-duplicate"
      >
        <Copy size={14} />
        <span>{t('sidebar.duplicateSelected')}</span>
      </button>
      <button
        type="button"
        disabled={!canDelete}
        title={readOnly ? t('sidebar.selectionReadOnly') : undefined}
        onClick={onDelete}
        className="ctx-menu-btn ctx-menu-btn--danger"
      >
        <Trash2 size={14} />
        <span>{t('sidebar.deleteSelected')}</span>
      </button>
    </div>
  );
}
